import { useLazyQuery } from '@apollo/client';
import { Empty, Select } from 'antd';
import { debounce, uniqBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { LIMIT, SORT } from '../../../../../common/constants';
import SelectComponent from '../../../../../components/SelectComponent';
import { LIBRARY_USERS } from '../../../../../libraryModules/orders/modules/new/graphql/Queries';
import { USERS } from '../graphql/Queries';

const { Option } = Select;

const UserSelect = (props) => {
  const {
    userData,
    roles,
    isModalOpen = true,
    noPopupContainer = false,
    isLibrary = false,
    ...rest
  } = props;

  const [userList, setUserList] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [isAllUsersFetched, setIsAllUsersFetched] = useState(false);
  const [isUsersAllowClear, setIsUsersAllowClear] = useState(false);
  const [isUsersDropdownVisible, setIsUsersDropdownVisible] = useState(false);
  const [isFetchMoreUsersLoading, setIsFetchMoreUsersLoading] = useState(false);

  const updateUserList = (newUsers) => {
    if (newUsers?.length < LIMIT) {
      setIsAllUsersFetched(true);
    }
    setIsFetchMoreUsersLoading(false);
    if (userData) {
      setUserList(
        uniqBy(
          [
            {
              firstName: userData?.firstName,
              lastName: userData?.lastName,
              id: userData?.id,
              userNumber: userData?.userNumber,
            },
            ...userList,
            ...newUsers,
          ],
          'id',
        ),
      );
      return;
    }
    setUserList([...userList, ...newUsers]);
  };

  const [users, { loading: isUserLoading }] = useLazyQuery(USERS, {
    onCompleted: (response) => {
      updateUserList(response?.users?.data || []);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [libraryUsers, { loading: isLibraryUserLoading }] = useLazyQuery(
    LIBRARY_USERS,
    {
      onCompleted: (response) => {
        updateUserList(response?.libraryUsers?.data || []);
      },
      fetchPolicy: 'network-only',
      onError() {},
    },
  );

  // Determine which query function and loading state to use
  const queryFunction = isLibrary ? libraryUsers : users;
  const loading = isLibrary ? isLibraryUserLoading : isUserLoading;

  useEffect(() => {
    setUserSearchTerm('');
    setUserList([]);
    if (isModalOpen) {
      queryFunction({
        variables: {
          filter: {
            limit: LIMIT,
            skip: 0,
            search: userSearchTerm,
            roles,
            isActive: true,
            isArchived: false,
          },
          sort: {
            field: 'createdAt',
            order: SORT?.DESC,
          },
        },
      });
    }
    setIsAllUsersFetched(false);
  }, []);

  const handleUsersScroll = (event) => {
    if (!isAllUsersFetched && !loading) {
      const target = event?.target;
      const currentLength = userList?.length;
      if (
        target?.scrollTop + target?.offsetHeight >=
        target?.scrollHeight - 5
      ) {
        setIsFetchMoreUsersLoading(true);
        queryFunction({
          variables: {
            filter: {
              isActive: true,
              isArchived: false,
              limit: LIMIT,
              skip: currentLength,
              search: userSearchTerm,
              roles,
            },
            sort: {
              field: 'createdAt',
              order: SORT?.DESC,
            },
          },
        });
      }
    }
  };

  const handleUsersSearch = (searchTerm) => {
    const trimSearch = searchTerm?.trim();
    setIsAllUsersFetched(false);
    setUserList([]);
    setUserSearchTerm(trimSearch);
    queryFunction({
      variables: {
        filter: {
          isActive: true,
          isArchived: false,
          limit: LIMIT,
          skip: 0,
          search: trimSearch,
          roles,
        },
        sort: {
          field: 'createdAt',
          order: SORT?.DESC,
        },
      },
    });
  };

  const handleUsersScrollDebounce = debounce(handleUsersScroll, 500);
  const handleSearchUsersDebounce = debounce(handleUsersSearch, 500);

  useEffect(() => {
    handleUsersScrollDebounce?.cancel();
  }, [handleUsersScrollDebounce]);

  useEffect(() => {
    handleSearchUsersDebounce?.cancel();
  }, [handleSearchUsersDebounce]);

  useEffect(() => {
    if (loading) {
      setIsUsersAllowClear(false);
    } else {
      setIsUsersAllowClear(true);
    }
  }, [loading]);

  return (
    <SelectComponent
      noPopupContainer={noPopupContainer}
      showSearch
      loading={(loading && isUsersDropdownVisible) || isFetchMoreUsersLoading}
      placeholder="Select User"
      optionFilterProp="children"
      allowClear={isUsersAllowClear}
      onPopupScroll={handleUsersScrollDebounce}
      onSearch={handleSearchUsersDebounce}
      onDropdownVisibleChange={(visible) => setIsUsersDropdownVisible(visible)}
      filterOption={false}
      notFoundContent={
        !isFetchMoreUsersLoading && loading ? (
          <span>Loading...</span>
        ) : (
          <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
        )
      }
      {...rest}
    >
      {userList?.map((user) => (
        <Option key={user?.id} value={user?.id}>
          {user?.firstName || user?.lastName || user?.userNumber
            ? `${user?.firstName || ''} ${user?.lastName || ''} (${
                user?.userNumber
              })`
            : '-'}
        </Option>
      ))}
    </SelectComponent>
  );
};

export default UserSelect;
